import { Box, Paper, Typography, useTheme } from '@mui/material'
import { useLocation, useNavigate } from 'react-router-dom'
import { PageURLs } from 'Routes'
import { MOBILE_NAV_BAR_HEIGHT, mobileNavSafeBottom } from '../../constants/mobileNav'
import HomeRoundedIcon from '@mui/icons-material/HomeRounded'
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined'
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded'
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined'
import CategoryRoundedIcon from '@mui/icons-material/CategoryRounded'
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined'
import AssignmentRoundedIcon from '@mui/icons-material/AssignmentRounded'
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined'
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded'
import { motion, AnimatePresence } from 'framer-motion'

const NavItem = ({ icon, activeIcon, label, path, active, onClick }) => {
  const theme = useTheme()

  return (
    <Box
      component={motion.div}
      whileTap={{ scale: 0.92 }}
      onClick={() => onClick(path)}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 1,
        cursor: 'pointer',
        py: 1,
        position: 'relative',
        gap: 0.25,
      }}
    >
      {/* Active indicator pill */}
      <AnimatePresence>
        {active && (
          <Box
            component={motion.div}
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.6 }}
            transition={{ type: 'spring', stiffness: 500, damping: 30 }}
            sx={{
              position: 'absolute',
              top: 6,
              width: 40,
              height: 40,
              borderRadius: '50%',
              background: `${theme.palette.primary.main}18`,
              zIndex: 0,
            }}
          />
        )}
      </AnimatePresence>

      <Box
        component={motion.div}
        animate={{ y: active ? -1 : 0 }}
        transition={{ type: 'spring', stiffness: 400, damping: 25 }}
        sx={{
          zIndex: 1,
          color: active ? theme.palette.primary.main : theme.palette.text.secondary,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {active ? activeIcon : icon}
      </Box>

      <Typography
        variant="caption"
        sx={{
          fontSize: '0.625rem',
          fontWeight: active ? 700 : 500,
          color: active ? theme.palette.primary.main : theme.palette.text.secondary,
          letterSpacing: '0.03em',
          lineHeight: 1,
          zIndex: 1,
          transition: 'color 0.2s ease',
        }}
      >
        {label}
      </Typography>
    </Box>
  )
}

const MobileBottomNav = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const theme = useTheme()

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/'
    return location.pathname.startsWith(path)
  }

  const handleNav = (path) => {
    navigate(path)
  }

  const navItems = [
    {
      label: 'Home',
      path: '/',
      icon: <HomeRoundedIcon sx={{ fontSize: 24 }} />,
      activeIcon: <HomeRoundedIcon sx={{ fontSize: 24 }} />,
    },
    {
      label: 'Products',
      path: PageURLs.ProductListing,
      icon: <Inventory2OutlinedIcon sx={{ fontSize: 24 }} />,
      activeIcon: <Inventory2RoundedIcon sx={{ fontSize: 24 }} />,
    },
    {
      label: 'Category',
      path: PageURLs.Categories,
      icon: <CategoryOutlinedIcon sx={{ fontSize: 24 }} />,
      activeIcon: <CategoryRoundedIcon sx={{ fontSize: 24 }} />,
    },
    {
      label: 'Product Request',
      path: PageURLs.SupportNew,
      icon: <AssignmentOutlinedIcon sx={{ fontSize: 24 }} />,
      activeIcon: <AssignmentRoundedIcon sx={{ fontSize: 24 }} />,
    },
    {
      label: 'Orders',
      path: PageURLs.OrderHistory,
      icon: <ReceiptLongOutlinedIcon sx={{ fontSize: 24 }} />,
      activeIcon: <ReceiptLongRoundedIcon sx={{ fontSize: 24 }} />,
    },
  ]

  return (
    <Paper
      component={motion.div}
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ type: 'spring', stiffness: 300, damping: 30, delay: 0.1 }}
      elevation={0}
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: (theme) => theme.zIndex.drawer - 1,
        borderRadius: '20px 20px 0 0',
        display: { xs: 'flex', md: 'none' },
        minHeight: MOBILE_NAV_BAR_HEIGHT,
        pb: mobileNavSafeBottom,
        background: theme.palette.mode === 'dark'
          ? 'rgba(15, 22, 41, 0.95)'
          : 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        border: 'none',
        borderTop: `1px solid ${
          theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)'
        }`,
        boxShadow: theme.palette.mode === 'dark'
          ? '0 -4px 24px rgba(0,0,0,0.45)'
          : '0 -4px 24px rgba(99,102,241,0.12)',
        overflow: 'hidden',
      }}
    >
      {navItems.map((item) => (
        <NavItem
          key={item.path}
          {...item}
          active={isActive(item.path)}
          onClick={handleNav}
        />
      ))}
    </Paper>
  )
}

export default MobileBottomNav
